import type { CoreLeadDetail } from '@/mocks/coreLeadDetails/types';
import type { TaxPolicy } from '@/mocks/taxPolicies';

export interface LeadDetailHints {
  publicFacts?: string;
  assumptionsToVerify?: string;
  dataGaps?: string[];
  serviceProducts?: string[];
  taxTypes?: string[];
  verificationAction?: string;
  evidenceLevel?: string;
  sourceIds?: string[];
  policyIds?: string[];
  serviceModule?: string;
  riskScore?: number | null;
  priorityScore?: number | null;
  maturityScore?: number | null;
  window?: string | null;
  deadline?: string | null;
  remainingDays?: number | null;
  policyStatus?: string | null;
  humanReview?: boolean | null;
  rankReason?: string | null;
  nextAction?: string | null;
  blockingItems?: string | null;
  serviceGapConfirmed?: string | null;
  /** 后端组装的详情八板块（对齐 demo） */
  detail?: CoreLeadDetail | null;
  /** 该线索底稿 Markdown（O06+关联节），详情页优先原文渲染 */
  markdown?: string | null;
}

export interface BatchClient {
  id: string;
  companyName: string;
  industry: string;
  industryTag: string;
  region: string;
  annualRevenue: string;
  employeeCount: number;
  /** 成立日期（来自 raw.basic.establishDate 等） */
  establishDate?: string;
  /** 人员规模原文（如「100-499人」）；优先于 employeeCount 展示） */
  staffSizeLabel?: string;
  serviceType: string;
  priority: 'high' | 'medium' | 'low';
  riskLevel: 'A' | 'B' | 'C' | 'D';
  opportunityScore: number;
  /** demo 浅查询：企业简介段落 */
  businessDescription?: string;
  /** demo 浅查询：税务画像 */
  taxProfile?: {
    taxpayerType: string;
    taxTypes: string[];
    industryPosition: string;
  };
  /** 浅查 preview.profile.policies 解析结果 */
  relatedPolicies?: TaxPolicy[];
  opportunityPortrait?: {
    primary: string;
    secondary: string[];
    reason: string;
  };
  opportunities: {
    /** 后端线索 id（O06 lead_id），优先于本地生成的 CL- 前缀 */
    id?: string;
    category: string;
    title: string;
    description: string;
    estimatedValue: string;
    urgency: 'urgent' | 'normal' | 'low';
    tags: string[];
    portraitType?: string;
    detailHints?: LeadDetailHints;
  }[];
  financialInsights: {
    label: string;
    value: string;
    benchmark: string;
    deviation: string;
    status: 'warning' | 'normal' | 'good';
  }[];
  conversationScript: {
    opening: string;
    painPoints: string[];
    valueProposition: string;
    closing: string;
  };
  createdAt: string;
  // 后端 LLM 生成的完整报告原文（markdown）
  reportMarkdown?: string;
  /** 浅查/全量 enrich 任务 ID，供 /reports/export-pdf 按 report_id 取库内正文 */
  enrichTaskId?: string;
}

// 商机线索优先级排序：紧急 > 常规 > 低优先级
export const URGENCY_RANK: Record<BatchClient['opportunities'][number]['urgency'], number> = {
  urgent: 0,
  normal: 1,
  low: 2,
};

export type OpportunityItem = BatchClient['opportunities'][number];

/** 按 紧急 > 常规 > 低优先级 稳定排序（同优先级保持原相对顺序）。 */
export function sortOpportunitiesByUrgency<T extends OpportunityItem>(list: T[]): T[] {
  return [...list].sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency]);
}
